import { authProviderSchema, idleTimeoutSchema, mfaTierSchema, ownershipRoleSchema, profileTypeSchema, } from '@teo/types';
import { z } from 'zod';
import { displayId } from './common.js';
export const signupErrorCodeSchema = z.enum([
    'signup_conflict',
    'active_account_exists',
    'soft_deleted_account_exists',
    'invite_code_invalid',
]);
export const inviteCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .length(8)
    .regex(/^[A-Z0-9]+$/);
export const registerSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    password: z.string().min(8),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    phone: z.string().nullable(),
    inviteCode: inviteCodeSchema,
});
export const loginSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    password: z.string(),
});
export const googleLoginSchema = z.object({
    credential: z.string(),
});
export const googleSignupSchema = z.object({
    credential: z.string(),
    inviteCode: inviteCodeSchema,
});
export const updateUserSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    phone: z.string().nullable(),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(12),
    confirmPassword: z.string().min(12),
});
export const refreshTokenSchema = z.object({
    refreshToken: z.string().min(1),
});
export const workosReturnPathSchema = z
    .string()
    .max(512)
    .refine((p) => p.startsWith('/') && !p.startsWith('//'), {
    message: 'Return path must be a relative path',
});
export const workosLoginUrlSchema = z.object({
    returnPath: workosReturnPathSchema.optional(),
});
export const workosLoginUrlResponseSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('enabled'), url: z.string().url() }),
    z.object({ status: z.literal('disabled') }),
]);
export const authkitCallbackSchema = z.object({
    code: z.string().min(1),
    state: z.string().min(1).optional(),
});
export const passwordManagementOutputSchema = z.discriminatedUnion('owner', [
    z.object({ owner: z.literal('teo') }),
    z.object({ owner: z.literal('workos') }),
    z.object({ owner: z.literal('none') }),
]);
export const workosPasswordResetResponseSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('ready'), url: z.string().url() }),
    z.object({ status: z.literal('unavailable') }),
]);
export const workosLogoutUrlResponseSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('enabled'), url: z.string().url() }),
    z.object({ status: z.literal('disabled') }),
]);
export const validateInviteCodeSchema = z.object({
    inviteCode: inviteCodeSchema,
});
export const createProfileInviteSchema = z.object({
    profileType: profileTypeSchema,
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().trim().toLowerCase().email(),
    expiresInDays: z.number().int().min(1).default(30),
    ownershipId: displayId.optional(),
    groupName: z.string().optional(),
    softDeletedUserAction: z
        .enum(['restore_previous_permissions', 'reinvite_with_new_permissions'])
        .optional(),
    softDeletedUserDeletedAt: z.string().datetime().optional(),
    ownershipChange: z
        .object({
        invitePercentage: z.number().min(-100).max(100),
        allocationChanges: z.record(z.string(), z.number()),
    })
        .refine((data) => {
        const allocationSum = Object.values(data.allocationChanges).reduce((acc, cur) => acc + cur, 0);
        return (data.invitePercentage >= 0 &&
            data.invitePercentage <= 100 &&
            allocationSum + data.invitePercentage === 0);
    }, {
        message: 'Invite percentage must be between 0 and 100, and allocation changes must sum to the negative of invite percentage (to balance to zero)',
        path: ['invitePercentage', 'allocationChanges'],
    })
        .optional(),
});
export const inviteUserToOwnershipSchema = z.object({
    ownershipId: displayId,
    email: z.string().trim().toLowerCase().email(),
    firstName: z.string(),
    lastName: z.string(),
    role: ownershipRoleSchema,
    expiresInDays: z.number().int().min(1).default(30),
    groupName: z.string().optional(),
    ownershipChange: z
        .object({
        invitePercentage: z.number().min(-100).max(100),
        allocationChanges: z.record(z.string(), z.number()),
    })
        .refine((data) => {
        const allocationSum = Object.values(data.allocationChanges).reduce((acc, cur) => acc + cur, 0);
        return (data.invitePercentage >= 0 &&
            data.invitePercentage <= 100 &&
            allocationSum + data.invitePercentage === 0);
    }, {
        message: 'Invite percentage must be between 0 and 100, and allocation changes must sum to the negative of invite percentage (to balance to zero)',
        path: ['invitePercentage', 'allocationChanges'],
    })
        .optional(),
});
export const actionSchema = z.enum(['add', 'remove']);
export const updateUserProfileTypeSchema = z.object({
    userId: displayId,
    profileType: profileTypeSchema,
    action: actionSchema,
});
export const deleteUserSchema = z.object({
    userId: displayId,
});
export const deleteProfileInviteSchema = z.object({
    inviteId: displayId,
});
export const authUserResponseSchema = z.object({
    id: z.number(),
    displayId: z.string(),
    email: z.string().email(),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    phone: z.string().nullable(),
    profiles: z.array(profileTypeSchema),
    authProvider: authProviderSchema,
    idleTimeout: idleTimeoutSchema,
});
export const userWithTokenResponseSchema = z.object({
    user: authUserResponseSchema,
    token: z.string(),
    refreshToken: z.string().optional(),
});
export const userOnlyResponseSchema = z.object({
    user: authUserResponseSchema,
});
export const authkitCallbackResponseSchema = userWithTokenResponseSchema.extend({
    returnPath: workosReturnPathSchema,
});
export const googleAuthOutputSchema = z.object({
    token: z.string(),
    user: authUserResponseSchema,
    refreshToken: z.string().optional(),
    isNewUser: z.boolean().optional(),
});
export const jwtPayloadSchema = z.object({
    sub: displayId,
    iat: z.number(),
    exp: z.number(),
    jti: z.string(),
    aud: z.string(),
    id: z.number(),
    displayId: z.string(),
    email: z.string().email(),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    phone: z.string().nullable(),
    profiles: z.array(profileTypeSchema),
    authProvider: authProviderSchema,
});
export const listProfileInvitesOutputSchema = z.array(z.object({
    id: z.string(),
    inviteCode: z.string(),
    profileType: profileTypeSchema,
    expiresAt: z.string().optional(),
    isUsed: z.boolean(),
    email: z.string(),
    createdAt: z.string(),
}));
export const createProfileInviteOutputSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('invite_created'),
        profileType: profileTypeSchema,
        inviteCode: z.string(),
        expiresAt: z.string().optional(),
    }),
    z.object({
        status: z.literal('soft_deleted_user_confirmation_required'),
        message: z.string(),
        adminMessage: z.string(),
        softDeletedUserDeletedAt: z.string().datetime(),
    }),
    z.object({
        status: z.literal('restored_soft_deleted_user'),
        message: z.string(),
    }),
]);
export const inviteUserToOwnershipOutputSchema = z.object({
    success: z.boolean(),
    message: z.string(),
});
export const listAllUsersOutputSchema = z.object({
    users: z.array(z.object({
        userDisplayId: displayId,
        email: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        profiles: z.array(profileTypeSchema),
        authProvider: z.string(),
        workosUserId: z.string().nullable(),
        idleTimeout: idleTimeoutSchema,
        createdAt: z.string(),
    })),
});
export const validateInviteCodeOutputSchema = z.discriminatedUnion('valid', [
    z.object({
        valid: z.literal(true),
        profileType: profileTypeSchema,
        email: z.string(),
    }),
    z.object({
        valid: z.literal(false),
    }),
]);
export const updateProfileInputSchema = z.object({
    profileType: profileTypeSchema,
});
export const impersonateUserInputSchema = z.object({
    impersonateDisplayUserId: z.string(),
});
export const enrolledFactorKindSchema = z.enum([
    'authenticator-app',
    'text-message',
    'one-time-code',
]);
export const mfaStatusOutputSchema = z.object({
    tier: mfaTierSchema,
    required: z.boolean(),
    reason: z.string(),
    enrolledFactors: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('known'), factors: z.array(enrolledFactorKindSchema) }),
        z.object({ kind: z.literal('unavailable') }),
    ]),
    overriddenBy: z
        .object({
        note: z.string(),
        setByUserId: z.number().int(),
        setAt: z.string(),
    })
        .optional(),
});
export const adminMfaStatusInputSchema = z.object({ userId: displayId });
export const adminSetMfaOverrideSchema = z.object({
    userId: displayId,
    kind: z.enum(['require', 'exempt']),
    note: z.string().min(1).max(280),
    acknowledgedFactorRemoval: z.boolean().optional(),
});
export const adminClearMfaOverrideSchema = z.object({ userId: displayId });
export const adminIdleTimeoutInputSchema = z.object({ userId: displayId });
export const adminSetIdleTimeoutSchema = z.object({
    userId: displayId,
    idleTimeout: idleTimeoutSchema,
});
export const adminIdleTimeoutOutputSchema = z.object({
    idleTimeout: idleTimeoutSchema,
    lastChange: z
        .object({
        changedBy: z.string().optional(),
        changedAt: z.string().datetime(),
    })
        .optional(),
});
export const adminResetAuthenticatorSchema = z.object({
    userId: displayId,
    note: z.string().min(1).max(280),
});
export const adminResetAuthenticatorOutputSchema = z.object({
    removed: z.number().int(),
    status: mfaStatusOutputSchema,
});
//# sourceMappingURL=auth.js.map